const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

export function computeLossPct(sent, received) {
  if (sent <= 0) return 0;
  if (received >= sent) return 0;
  return ((sent - received) / sent) * 100;
}

// RFC 3550 jitter update:
//   J = J + (|D(i-1, i)| - J) / 16
// where D(i-1, i) = (Rj - Ri) - (Sj - Si)
// In our case all timestamps are ms and `transit = arrived - sent`.
export function updateJitter(prevJitter, transitNow, prev) {
  if (!prev) return 0;
  const diff = Math.abs(transitNow - prev.transit);
  return prevJitter + (diff - prevJitter) / 16;
}

function percentile(samples, p) {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a - b);
  // Nearest-rank percentile per the standard form: ceil(p/100 * n) - 1, clamped.
  // Matches the formula used in src/probes/websocket.js.
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx];
}

export function summarizeWebrtc({ sent, received, jitter, rttSamples }) {
  const completed = sent > 0 && received > 0;
  return {
    sent,
    received,
    completed,
    packetLossPct: completed ? computeLossPct(sent, received) : null,
    jitterMs: completed ? jitter : null,
    rttMs: completed ? percentile(rttSamples, 50) : null,
    rttP50Ms: completed ? percentile(rttSamples, 50) : null,
    rttP95Ms: completed ? percentile(rttSamples, 95) : null,
    rttMinMs: completed ? (rttSamples.length ? Math.min(...rttSamples) : 0) : null,
    rttMaxMs: completed ? (rttSamples.length ? Math.max(...rttSamples) : 0) : null,
    rttSamples
  };
}

export function runWebrtcProbe({
  signalingUrl,
  durationMs,
  packetsPerSecond = 50,
  WebSocketImpl = WebSocket,
  RTCPeerConnectionImpl = RTCPeerConnection,
  onProgress
}) {
  return new Promise(async (resolve, reject) => {
    let signalSocket;
    let pc;
    let dc;
    let sendTimer = null;
    let stopTimer = null;
    let stopped = false;

    let seq = 0;
    let received = 0;
    let jitter = 0;
    let prev = null;
    const rttSamples = [];
    const sentTimes = new Map();

    const cleanup = () => {
      stopped = true;
      if (sendTimer) clearInterval(sendTimer);
      if (stopTimer) clearTimeout(stopTimer);
      try { dc?.close(); } catch {}
      try { pc?.close(); } catch {}
      try { signalSocket?.close(); } catch {}
    };

    const finalize = () => {
      cleanup();
      const summary = summarizeWebrtc({
        sent: seq,
        received,
        jitter,
        rttSamples
      });
      resolve(summary);
    };

    stopTimer = setTimeout(finalize, durationMs);

    try {
      signalSocket = new WebSocketImpl(signalingUrl);

      signalSocket.addEventListener('open', async () => {
        try {
          pc = new RTCPeerConnectionImpl({ iceServers: ICE_SERVERS });
          dc = pc.createDataChannel('probe', { ordered: false, maxRetransmits: 0 });
          dc.binaryType = 'arraybuffer';

          pc.addEventListener('icecandidate', ({ candidate }) => {
            if (candidate) {
              try { signalSocket.send(JSON.stringify({ type: 'ice', candidate })); } catch {}
            }
          });

          dc.addEventListener('open', () => {
            const intervalMs = 1000 / packetsPerSecond;
            sendTimer = setInterval(() => {
              if (stopped || dc.readyState !== 'open') return;
              const sentAt = Date.now();
              seq += 1;
              sentTimes.set(seq, sentAt);
              try {
                dc.send(JSON.stringify({ seq, t: sentAt }));
              } catch {
                // Channel closed mid-send; the close listener will reject if needed.
              }
              if (onProgress) onProgress({ sent: seq, received });
            }, intervalMs);
          });

          dc.addEventListener('message', e => {
            let parsed;
            try { parsed = JSON.parse(typeof e.data === 'string' ? e.data : new TextDecoder().decode(e.data)); } catch { return; }
            if (typeof parsed?.seq !== 'number') return;
            const sentAt = sentTimes.get(parsed.seq);
            if (sentAt === undefined) return;
            sentTimes.delete(parsed.seq);
            const arrivedAt = Date.now();
            const rtt = arrivedAt - sentAt;
            const transit = arrivedAt - parsed.t;
            jitter = updateJitter(jitter, transit, prev);
            prev = { transit };
            rttSamples.push(rtt);
            received += 1;
          });

          dc.addEventListener('close', () => {
            if (!stopped) finalize();
          });

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          signalSocket.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription }));
        } catch (err) {
          cleanup();
          reject(err);
        }
      });

      signalSocket.addEventListener('message', async e => {
        let msg;
        try { msg = JSON.parse(typeof e.data === 'string' ? e.data : ''); } catch { return; }
        try {
          if (msg.type === 'answer') {
            await pc.setRemoteDescription(msg.sdp);
          } else if (msg.type === 'ice' && msg.candidate) {
            await pc.addIceCandidate(msg.candidate);
          } else if (msg.type === 'error') {
            cleanup();
            reject(new Error(`signaling error: ${msg.message}`));
          }
        } catch (err) {
          cleanup();
          reject(err);
        }
      });

      signalSocket.addEventListener('error', err => {
        cleanup();
        reject(err instanceof Error ? err : new Error('signaling socket error'));
      });

      signalSocket.addEventListener('close', () => {
        if (!stopped) finalize();
      });
    } catch (err) {
      cleanup();
      reject(err);
    }
  });
}
